// Core
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import Logo from "components/Logo";

function Error404() {
  const { t } = useTranslation(["common"]);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // App-aligned colors
  const bg = isDark ? "#0f1419" : "#f8f9fa";
  const textPrimary = isDark ? "#f0f4f8" : "#1a2332";
  const textSecondary = isDark ? "#8a98ad" : "#67748e";

  // App accent: teal (info) → green (success)
  const accentFrom = isDark ? "#00bfa5" : "#17c1e8";
  const accentTo = isDark ? "#4caf50" : "#82d616";
  const blobColor1 = isDark ? "rgba(0,191,165,0.15)" : "rgba(23,193,232,0.12)";
  const blobColor2 = isDark ? "rgba(76,175,80,0.12)" : "rgba(130,214,22,0.10)";

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: bg,
        overflow: "hidden",
      }}
    >
      {/* Decorative blobs — app-colored */}
      <Box
        sx={{
          position: "absolute",
          width: 480,
          height: 480,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${blobColor1} 0%, transparent 70%)`,
          top: "-8%",
          right: "-4%",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 380,
          height: 380,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${blobColor2} 0%, transparent 70%)`,
          bottom: "-4%",
          left: "-4%",
          pointerEvents: "none",
        }}
      />

      {/* Main content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          maxWidth: 560,
          px: 3,
          gap: 2,
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 1 }}>
          <Logo size="small" showSubtitle={false} align="center" variant="v1" />
        </Box>

        {/* 404 number */}
        <Typography
          sx={{
            fontSize: { xs: "7rem", sm: "10rem", md: "12rem" },
            fontWeight: 900,
            lineHeight: 1,
            background: `linear-gradient(135deg, ${accentFrom} 0%, ${accentTo} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            letterSpacing: "-0.04em",
            userSelect: "none",
          }}
        >
          404
        </Typography>

        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            color: textPrimary,
            fontWeight: 700,
            mt: -2,
            letterSpacing: "-0.01em",
          }}
        >
          {t("not_found.title")}
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="body1"
          sx={{
            color: textSecondary,
            maxWidth: 400,
            lineHeight: 1.7,
          }}
        >
          {t("not_found.description")}
        </Typography>

        {/* Buttons */}
        <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap", justifyContent: "center" }}>
          <Button
            variant="contained"
            onClick={() => navigate("/communities")}
            sx={{
              background: `linear-gradient(135deg, ${accentFrom}, ${accentTo})`,
              color: "#fff",
              fontWeight: 700,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              boxShadow: isDark
                ? "0 4px 24px rgba(0,191,165,0.30)"
                : "0 4px 16px rgba(23,193,232,0.25)",
              "&:hover": {
                background: `linear-gradient(135deg, ${accentTo}, ${accentFrom})`,
                boxShadow: isDark
                  ? "0 6px 28px rgba(0,191,165,0.40)"
                  : "0 6px 20px rgba(23,193,232,0.35)",
              },
            }}
          >
            {t("not_found.home")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              color: textSecondary,
              borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              "&:hover": {
                borderColor: isDark ? accentFrom : accentFrom,
                backgroundColor: isDark ? "rgba(0,191,165,0.08)" : "rgba(23,193,232,0.06)",
              },
            }}
          >
            {t("back")}
          </Button>
        </Box>
      </Box>

      {/* Bottom hint */}
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          bottom: 32,
          color: textSecondary,
          opacity: 0.5,
        }}
      >
        {t("not_found.footer")}
      </Typography>
    </Box>
  );
}

export default Error404;
